import Axios, {
  AxiosBasicCredentials,
  AxiosInstance,
  AxiosProxyConfig,
  AxiosRequestConfig,
} from 'axios';
import * as React from 'react';
import { RootContext } from '../contexts/root';
import { HttpErrorModel } from '../models/http-error';
import { log } from '../utils/log';
import { objectFindValueFromSomeKey } from '../utils/object/object-find-value-from-some-key';
import { objectToMap } from '../utils/object/object-to-map';
import { StorageUtil } from '../utils/storage';
import { defaultIfEmpty } from '../utils/string/helpers';
import { useAuth } from './auth';

type RequestParamValue = string | number | boolean | undefined;

interface HeaderWithAuthorization {
  Authorization: string;
}

interface HttpPossibleErrorData {
  [key: string]: string | undefined;
  // error?: string;
  // message?: string;
  // details?: string;

  // erro?: string;
  // mensagem?: string;
  // mensagemDesenvolvedor?: string;
  // mensagemUsuario?: string;

  // detail?: string;
  // title?: string;
  // type?: string;
  // instance?: string;

  // parameters?: {...extensions}
  // ...extensions
}

interface HttpPossibleError {
  response?: {
    status?: string | number;
    headers?: { 'content-type': string };
    data?: HttpPossibleErrorData | HttpPossibleErrorData[];
  };
  code?: string | number;
  message?: string;
}

export type RequestParams =
  | { [key: string]: RequestParamValue }
  | Map<string, RequestParamValue>
  | URLSearchParams;

function sanitizeUrl(url: string): string {
  return url.trim().replace(/\/$/, '');
}

function configFactory(config?: HookHttpConfigInterface): AxiosRequestConfig {
  const axiosConfig: AxiosRequestConfig = {};
  if (!!config?.headers) {
    axiosConfig.headers = config.headers;
  }
  if (!!config?.baseUrl) {
    axiosConfig.baseURL = sanitizeUrl(config.baseUrl);
  }
  if (!!config?.withCredentials) {
    axiosConfig.withCredentials = config.withCredentials;
  }
  if (!!config?.authBasicCredentials) {
    axiosConfig.auth = config.authBasicCredentials;
  }
  if (!!config?.proxyConfig) {
    axiosConfig.proxy = config.proxyConfig;
  }
  // log('Axios ', 'Config::Factory', {
  //   ...axiosConfig,
  // });
  return axiosConfig;
}

function errorCodeFromStatusCode(statusCode: number): string {
  const errorCodes = {
    400: 'core.bad-request',
    401: 'core.unauthorized',
    403: 'core.forbidden',
    404: 'core.not-found',
    405: 'core.method-not-allowed',
    429: 'core.too-many-requests',
    500: 'core.interna-server-error',
    503: 'core.service-unavailable',
    504: 'core.gateway-timeout',
  };

  return (
    errorCodes?.[statusCode as keyof typeof errorCodes] ??
    'core.unexpected-error'
  );
}

function messageFromStatusCode(statusCode: number): string {
  const messages = {
    400: 'Xii, um erro negocial aconteceu',
    401: 'Xii, sua sessão expirou ou você não está logado',
    403: 'Xii, acesso negado',
    404: 'Xii, a informação não foi encontrada',
    405: 'Xii, problema na requisição',
    429: 'Xii, teve muitas requisições',
    500: 'Xii, ocorreu um erro interno no servidor',
    503: 'Xii, o serviço indisponível',
    504: 'Xii, o tempo de resposta expirou',
  };

  return (
    messages?.[statusCode as keyof typeof messages] ??
    'Xii, um erro inesperado aconteceu'
  );
}

function errorFactory(httpError?: HttpPossibleError): HttpErrorModel {
  const contentType = httpError?.response?.headers?.['content-type'] ?? '';

  let statusCode = +(httpError?.response?.status ?? httpError?.code ?? 0);
  if (isNaN(statusCode)) {
    statusCode = 0;
  }

  const data = httpError?.response?.data ?? [];

  const errorData = Array.isArray(data) ? data : [data];

  const errorKeys = [
    'error',
    'mensagemDesenvolvedor',
    'erro',
    'type',
    'instance',
  ];
  const messageKeys = [
    'detail',
    'title',
    'message',
    'mensagemUsuario',
    'mensagem',
    'details',
  ];

  const errorCode = defaultIfEmpty(
    errorData
      .map((datum) => objectFindValueFromSomeKey(datum, errorKeys))
      .join('|') ?? '',
    errorCodeFromStatusCode(statusCode),
  );
  const message = defaultIfEmpty(
    errorData
      .map((datum) => objectFindValueFromSomeKey(datum, messageKeys))
      .join('\n') ?? '',
    messageFromStatusCode(statusCode),
  );

  const error = new HttpErrorModel(message, statusCode, errorCode);

  const isProblemDetail =
    !Array.isArray(data) && contentType.includes('application/problem+json');

  if (isProblemDetail) {
    const clonedData: { [key: string]: unknown } = structuredClone(data);

    if ('type' in clonedData) clonedData.type = undefined;
    if ('status' in clonedData) clonedData.status = undefined;
    if ('title' in clonedData) clonedData.title = undefined;
    if ('detail' in clonedData) clonedData.detail = undefined;
    if ('instance' in clonedData) clonedData.instance = undefined;
    if ('type' in clonedData) clonedData.type = undefined;

    const extensions = new Map();
    const entriesExtensions = clonedData.parameters
      ? Object.entries(clonedData.parameters)
      : Object.entries(clonedData);

    entriesExtensions
      .filter(([, value]) => value !== undefined)
      .forEach(([key, value]) => extensions.set(key, value));

    if (extensions.size > 0) error.extensions = extensions;
  }

  log('HTTP ⛔ Error', contentType, error.toObject());
  return error;
}

function registerInterceptors(axiosInstance: AxiosInstance) {
  const request = axiosInstance.interceptors.request.use(
    (config) => {
      const protectedRequest =
        !!config?.withCredentials ||
        !!config?.auth ||
        !!(config?.headers as HeaderWithAuthorization)?.Authorization;

      log(
        'HTTP%s',
        `${
          protectedRequest ? ' 🔐' : ''
        } ${config?.method?.toUpperCase?.() ?? ''}`,
        config.url,
      );

      return config;
    },
    (error?: HttpPossibleError) => Promise.reject(errorFactory(error)),
  );

  const response = axiosInstance.interceptors.response.use(
    (response) => response,
    (error?: HttpPossibleError) => Promise.reject(errorFactory(error)),
  );

  return [request, response];
}

function unregisterInterceptors(
  axiosInstance: AxiosInstance,
  interceptors: number[],
) {
  const [request, response] = interceptors;
  axiosInstance.interceptors.request.eject(request);
  axiosInstance.interceptors.response.eject(response);
}

function requestUrlWithParams(
  url: string,
  params: RequestParams = new Map(),
): string {
  const paramMap = params instanceof Map ? params : objectToMap(params);
  if (paramMap?.size > 0) {
    let paramCounter = url.includes('?') ? 1 : 0;
    paramMap?.forEach((value, key) => {
      if (value !== undefined) {
        url += paramCounter++ > 0 ? '&' : '?';
        url += `${key}=${encodeURIComponent(value.toString())}`;
      }
    });
  }
  return url;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type HookHttpHeadersType = { [key: string]: string };
export type HookHttpBaseUrlType = string;
export type HookHttpWithCredentialsType = boolean;
export type HookHttpAuthBasicCredentialsType = AxiosBasicCredentials;
export type HookHttpProxyConfigType = AxiosProxyConfig;
export type HookHttpInjectTokenType = boolean;

export type HookHttpRequestWithoutBodyType<Response> = (
  url: string,
  params: RequestParams,
) => Promise<Response>;

export type HookHttpRequestWithBodyType<Response, Payload> = (
  url: string,
  body?: Payload,
  params?: RequestParams,
) => Promise<Response>;

export interface ResponseWithHeaders<Data> {
  headers: { [key: string]: string };
  data?: Data;
}

export interface HookHttpInterface {
  httpGet<Response>(url: string, params?: RequestParams): Promise<Response>;
  httpGetWithHeaders<Data>(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpGetText(url: string, params?: RequestParams): Promise<string>;
  httpGetTextWithHeaders(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<string>>;
  httpGetBlob(url: string, params?: RequestParams): Promise<Blob>;
  httpGetBlobWithHeaders(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Blob>>;
  httpDelete<Response>(url: string, params?: RequestParams): Promise<Response>;
  httpDeleteWithHeaders<Data>(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpHead<Response>(url: string, params?: RequestParams): Promise<Response>;
  httpHeadWithHeaders<Data>(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpOptions<Response>(url: string, params?: RequestParams): Promise<Response>;
  httpOptionsWithHeaders<Data>(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpPost<Response, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Response>;
  httpPostWithHeaders<Data, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpPostBlob<Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Blob>;
  httpPostBlobWithHeaders<Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Blob>>;
  httpPut<Response, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Response>;
  httpPutWithHeaders<Data, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpPatch<Response, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Response>;
  httpPatchWithHeaders<Data, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
}

export interface HookHttpConfigInterface {
  headers?: HookHttpHeadersType;
  baseUrl?: HookHttpBaseUrlType;
  withCredentials?: HookHttpWithCredentialsType;
  authBasicCredentials?: HookHttpAuthBasicCredentialsType;
  proxyConfig?: HookHttpProxyConfigType;
  injectToken?: HookHttpInjectTokenType;
}

const store = StorageUtil.inTemporaryStore();

export function useHttp(config?: HookHttpConfigInterface): HookHttpInterface {
  const { appName, appVersion } = React.useContext(RootContext);

  const { token } = useAuth();

  const cacheKey = React.useMemo(
    () => `× JWT × ${appName} @ ${appVersion}`,
    [appName, appVersion],
  );

  const configToAxios = React.useMemo(() => configFactory(config), [config]);

  const axiosInstance = React.useMemo(
    () => Axios.create(configToAxios),
    [configToAxios],
  );

  const axiosInstanceRef = React.useRef<AxiosInstance>(axiosInstance);

  const injectToken = React.useCallback(() => {
    const jwt = store.getItem<string>(cacheKey);
    if (!!config?.injectToken && !!jwt) {
      axiosInstanceRef.current.defaults.headers.Authorization = `Bearer ${jwt}`;
      log(
        'HTTP 🔏 JWT (cached)',
        jwt
          ? `${[jwt.substring(0, 5), jwt.substring(jwt.length - 5)].join(
              ' … ',
            )}`
          : '',
      );
    }
  }, [cacheKey, config?.injectToken]);

  const httpGet = React.useCallback(
    async <Response>(url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      const { data: result } =
        await axiosInstanceRef.current.get<Response>(url);
      return result;
    },
    [injectToken],
  );

  const httpGetWithHeaders = React.useCallback(
    async <Data>(url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { headers, data } = await axiosInstanceRef.current.get<Data>(url);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = { headers, data };
      return result as ResponseWithHeaders<Data>;
    },
    [injectToken],
  );

  const httpGetText = React.useCallback(
    async (url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      const { data: result } = await axiosInstanceRef.current.get<string>(url, {
        responseType: 'text',
      });
      return result;
    },
    [injectToken],
  );

  const httpGetTextWithHeaders = React.useCallback(
    async (url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { headers, data } = await axiosInstanceRef.current.get<string>(
        url,
        { responseType: 'text' },
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = { headers, data };
      return result as ResponseWithHeaders<string>;
    },
    [injectToken],
  );

  const httpGetBlob = React.useCallback(
    async (url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      const { data: result } = await axiosInstanceRef.current.get<Blob>(url, {
        responseType: 'blob',
      });
      return result;
    },
    [injectToken],
  );

  const httpGetBlobWithHeaders = React.useCallback(
    async (url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { headers, data } = await axiosInstanceRef.current.get<Blob>(url, {
        responseType: 'blob',
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = { headers, data };
      return result as ResponseWithHeaders<Blob>;
    },
    [injectToken],
  );

  const httpDelete = React.useCallback(
    async <Response>(url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      const { data: result } =
        await axiosInstanceRef.current.delete<Response>(url);
      return result;
    },
    [injectToken],
  );

  const httpDeleteWithHeaders = React.useCallback(
    async <Data>(url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { headers, data } =
        await axiosInstanceRef.current.delete<Data>(url);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = { headers, data };
      return result as ResponseWithHeaders<Data>;
    },
    [injectToken],
  );

  const httpHead = React.useCallback(
    async <Response>(url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      const { data: result } =
        await axiosInstanceRef.current.head<Response>(url);
      return result;
    },
    [injectToken],
  );

  const httpHeadWithHeaders = React.useCallback(
    async <Data>(url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { headers, data } = await axiosInstanceRef.current.head<Data>(url);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = { headers, data };
      return result as ResponseWithHeaders<Data>;
    },
    [injectToken],
  );

  const httpOptions = React.useCallback(
    async <Response>(url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      const { data: result } =
        await axiosInstanceRef.current.options<Response>(url);
      return result;
    },
    [injectToken],
  );

  const httpOptionsWithHeaders = React.useCallback(
    async <Data>(url: string, params: RequestParams = new Map()) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { headers, data } =
        await axiosInstanceRef.current.options<Data>(url);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = { headers, data };
      return result as ResponseWithHeaders<Data>;
    },
    [injectToken],
  );

  const httpPost = React.useCallback(
    async <Response, Payload>(
      url: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      const { data: result } = await axiosInstanceRef.current.post<Response>(
        url,
        body,
      );
      return result;
    },
    [injectToken],
  );

  const httpPostWithHeaders = React.useCallback(
    async <Data, Payload>(
      url: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { headers, data } = await axiosInstanceRef.current.post<Data>(
        url,
        body,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = { headers, data };
      return result as ResponseWithHeaders<Data>;
    },
    [injectToken],
  );

  const httpPostBlob = React.useCallback(
    async <Blob, Payload>(
      url: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      const { data: result } = await axiosInstanceRef.current.post<Blob>(
        url,
        body,
        { responseType: 'blob' },
      );
      return result;
    },
    [injectToken],
  );

  const httpPostBlobWithHeaders = React.useCallback(
    async <Blob, Payload>(
      url: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { headers, data } = await axiosInstanceRef.current.post<Blob>(
        url,
        body,
        { responseType: 'blob' },
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = { headers, data };
      return result as ResponseWithHeaders<Blob>;
    },
    [injectToken],
  );

  const httpPut = React.useCallback(
    async <Response, Payload>(
      url: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      const { data: result } = await axiosInstanceRef.current.put<Response>(
        url,
        body,
      );
      return result;
    },
    [injectToken],
  );

  const httpPutWithHeaders = React.useCallback(
    async <Data, Payload>(
      url: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { headers, data } = await axiosInstanceRef.current.put<Data>(
        url,
        body,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = { headers, data };
      return result as ResponseWithHeaders<Data>;
    },
    [injectToken],
  );

  const httpPatch = React.useCallback(
    async <Response, Payload>(
      url: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      const { data: result } = await axiosInstanceRef.current.patch<Response>(
        url,
        body,
      );
      return result;
    },
    [injectToken],
  );

  const httpPatchWithHeaders = React.useCallback(
    async <Data, Payload>(
      url: string,
      body: Payload = {} as Payload,
      params: RequestParams = new Map(),
    ) => {
      injectToken();
      url = requestUrlWithParams(url, params);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { headers, data } = await axiosInstanceRef.current.patch<Data>(
        url,
        body,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = { headers, data };
      return result as ResponseWithHeaders<Data>;
    },
    [injectToken],
  );

  React.useEffect(() => {
    const instance = axiosInstanceRef.current;
    const interceptors = registerInterceptors(instance);
    return () => unregisterInterceptors(instance, interceptors);
  }, []);

  React.useEffect(() => {
    if (!!token) {
      return store.setItem(cacheKey, token);
    }
    return store.removeItem(cacheKey);
  }, [token, cacheKey]);

  return {
    httpGet,
    httpGetWithHeaders,
    httpGetText,
    httpGetTextWithHeaders,
    httpGetBlob,
    httpGetBlobWithHeaders,
    httpDelete,
    httpDeleteWithHeaders,
    httpHead,
    httpHeadWithHeaders,
    httpOptions,
    httpOptionsWithHeaders,
    httpPost,
    httpPostWithHeaders,
    httpPostBlob,
    httpPostBlobWithHeaders,
    httpPut,
    httpPutWithHeaders,
    httpPatch,
    httpPatchWithHeaders,
  };
}
